import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import ReactQuill from "react-quill";
import {
  useAddImpactMutation,
  useUpdateImpactMutation,
} from "../api/impactsApi";
import { Impact, ImpactFormData } from "../types/impact";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  impact?: Impact | null;
}

const AddEditImpact: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  impact,
}) => {
  const [formData, setFormData] = useState<ImpactFormData>({
    title: "",
    description: "",
    value: "",
    image: null,
    existingImage: undefined,
  });

  const [addImpact] = useAddImpactMutation();
  const [updateImpact] = useUpdateImpactMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (impact) {
      setFormData({
        title: impact.title || "",
        description: impact.description || "",
        value: impact.value || "",
        image: null,
        existingImage: impact.image_url || undefined,
      });
    } else {
      setFormData({
        title: "",
        description: "<ul><li>Enter your first bullet point here...</li></ul>", // Default bullet list
        value: "",
        image: null,
        existingImage: undefined,
      });
    }
    setSubmissionError(null);
  }, [impact]);

  const handleInputChange = (field: string, value: any) => {
    // Special handling for image field to prevent empty objects
    if (field === "image" && (!value || !(value instanceof File))) {
      value = null;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDescriptionChange = (content: string) => {
    handleInputChange("description", content);
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      handleInputChange("image", files[0]);
    } else {
      handleInputChange("image", null);
    }
  };

  // Quill toolbar modules
  const modules = {
    toolbar: [
      [{ list: "bullet" }], // Bullet list button
      [{ list: "ordered" }], // Numbered list (optional)
      ["bold", "italic"], // Basic formatting
      ["clean"], // Remove formatting
    ],
  };

  const formats = ["list", "bold", "italic"];

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim()) {
      setSubmissionError("Impact title is required");
      return;
    }

    if (!formData.value.trim()) {
      setSubmissionError("Impact value is required");
      return;
    }

    // Validate description has content
    if (!formData.description || formData.description.trim() === "") {
      setSubmissionError("Description is required");
      return;
    }

    // For new impacts, require an image
    if (!impact && (!formData.image || !(formData.image instanceof File))) {
      setSubmissionError("Please select an image for the impact");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      // Final validation - ensure image is either null or a valid File
      if (formData.image && !(formData.image instanceof File)) {
        console.error("Invalid image detected:", formData.image);
        setSubmissionError("Please select a valid image file");
        setIsSaving(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description); // HTML content with bullets
      formDataToSend.append("value", formData.value);

      // Handle image field - only append if we have a valid File
      if (formData.image && formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
        console.log(
          "Appending image file:",
          formData.image.name,
          "Type:",
          formData.image.type
        );
      } else if (impact && formData.existingImage) {
        // For updates, if no new image is selected, don't send image field
        console.log(
          "Update mode: No new image selected, keeping existing image"
        );
      } else if (!impact) {
        setSubmissionError("Please select an image for the impact");
        setIsSaving(false);
        return;
      }

      console.log("Description HTML being sent:", formData.description);

      if (impact) {
        await updateImpact({
          id: impact.id.toString(),
          body: formDataToSend,
        }).unwrap();
      } else {
        await addImpact({
          body: formDataToSend,
        }).unwrap();
      }

      setModalOpen(false);
      // Reset form data after successful submission
      setFormData({
        title: "",
        description: "<ul><li>Enter your first bullet point here...</li></ul>",
        value: "",
        image: null,
        existingImage: undefined,
      });
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onSuccess?.();
    } catch (error: any) {
      console.error("Error saving impact:", error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "An error occurred while saving the impact";
      setSubmissionError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [formData, impact, addImpact, updateImpact, setModalOpen, onSuccess]);

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
    // Reset form data when closing
    if (!impact) {
      setFormData({
        title: "",
        description: "<ul><li>Enter your first bullet point here...</li></ul>",
        value: "",
        image: null,
        existingImage: undefined,
      });
    }
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{impact ? "Edit Impact" : "Add New Impact"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          <TextField
            label="Impact Title"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            fullWidth
            required
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              modules={modules}
              formats={formats}
              placeholder="Enter impact description as bullet points..."
              style={{ height: "150px" }}
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              Use the bullet list button above or press '*' + Enter to create
              bullets
            </Typography>
          </Box>

          <TextField
            label="Value"
            value={formData.value}
            onChange={(e) => handleInputChange("value", e.target.value)}
            fullWidth
            required
            placeholder=""
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Impact Image {!impact && <span style={{ color: "red" }}>*</span>}
            </Typography>
            <input
              ref={fileInputRef}
              key={impact ? `edit-${impact.id}` : "new"}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files)}
              style={{ marginBottom: "8px" }}
              required={!impact}
            />
            {formData.existingImage && !formData.image && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={formData.existingImage}
                  alt="Current image"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              </Box>
            )}
            {formData.image && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="success.main">
                  New image selected: {formData.image.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : impact ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditImpact;
